/**
 * [ScenarioConfigs] used for Step 4 of the Scenario module, handling access control configuration.
 * - Renders user and group access management UI for the current scenario.
 * - Uses local state for managing selection and syncing changes via props and callbacks.
 * - Loads user and group options via useScenarioConfigsData or lookup context.
 *
 * - All data-fetching and lookup logic is handled outside (usually via LookupsContext).
 * - Component focuses on rendering UI and bubbling up data changes.
 * - Deep clone before mutating baseline/config state to avoid mutating Redux.
 * - All updates are sent upward via onLocalScenarioChange for save handling by ScenarioMain.
 * - Uses verifyFields() where applicable to standardize validation.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.item - The current scenario being edited.
 * @param {string} props.mode - Controls editability of the form.
 * @param {string} [props.size] - UI size for text fields/selects.
 * @param {number} [props.formFieldWidth] - Width for select fields.
 * @param {boolean} props.dataChanged - Whether scenario data has been modified.
 * @param {number} props.groupId - Used to filter access options.
 * @param {function(Object):void} props.onLocalScenarioChange - Callback for local access config changes.
 * @param {function(boolean):void} props.onClearComplete - Callback to reset parent temp UI state.
 * @param {function(number, function):void} props.onSaveDataToDB - Callback to trigger DB save.
 * @returns {React.ReactElement}
 */

import React, { useState, useEffect, useRef } from "react";
import { useUpdateEffect } from "react-use";

// Custom hooks
import { useLookupsData } from "src/context/LookupsContext";

// Layout/UI
import { Box, Stack } from "@metrea-ui/v2";
import FormMultiSelect from "src/components/UI/common/FormMultiSelect";

// Utilities
import { deepClone, getValuesByPropertyPath } from "src/utils/dataUtils";
import { Session } from "src/utils/sessionUtils";
import { debugLog } from "src/utils/debugUtils";
import { updateAccessArray } from "src/utils/accessControlUtils";

const ScenarioConfigs = (props) => {
	const renderCount = useRef(0);
	renderCount.current++;

	const pageMode = props.mode;

	// Local copy of the current scenario (driven from props)
	const [currScenario, setCurrScenario] = useState(props.item);

	// Custom hooks
	const { allUsers, allUserGroups, sortedCfgTypes, updateSortedCfgTypes } = useLookupsData();

	// --- Effect: sync local when parent item changes
	useEffect(() => {
		debugLog(2, "[ScenarioConfigs][FLOW] mounted", {});
	}, []);

	useUpdateEffect(() => {
		setCurrScenario(props.item);
		debugLog(3, "[ScenarioConfigs][STATE] props.item changed", { propsItem: props.item });
	}, [props.item]);

	// --- Effect: Update sortedCfgTypes whenever groupId changes
	useEffect(() => {
		updateSortedCfgTypes(props.groupId);
		debugLog(3, "[ScenarioConfigs][STATE] groupId changed", { groupId: props.groupId });
	}, [props.groupId, updateSortedCfgTypes]);

	/**
	 * Handles updates for access control configs (users/groups).
	 * Updates baseline access arrays and notifies parent.
	 * @param {Array} selected - The selected access configs.
	 * @param {Object} cfgCategory - The config category object.
	 * @returns {void}
	 */
	const updateAccessHandler = (selected, cfgCategory) => {
		debugLog(3, "[ScenarioConfigs][ACTION] updateAccessHandler", {
			cfgCategory: cfgCategory,
			selectedCount: selected?.length ?? 0,
		});
		const arrayTemp = updateAccessArray(
			currScenario.scenario_accesses,
			selected,
			cfgCategory.id,
			currScenario.id, // Scenario ID
			Session().currUser.id,
			"scenario_id", // Foreign key for Scenario
		);
		const cloneScenario = { ...deepClone(currScenario), scenario_accesses: arrayTemp };

		const updateData = {
			...cloneScenario,
			scenario_accesses: [...arrayTemp],
			[`cfg_${cfgCategory.code}`]: arrayTemp.filter((x) => {
				return x.comp_type_id === cfgCategory.id && x.update_flag !== 103;
			}),
			updatedAt: new Date(),
			updatedBy: Session().currUser.id,
		};

		debugLog(4, "[ScenarioConfigs][PROCESS] updateAccessHandler.merge", {
			selected: selected,
			cfgCategory: cfgCategory,
			updateData: updateData,
		});

		setCurrScenario(updateData);
		props.onLocalScenarioChange(updateData);

		handleClearComplete();
	};

	const handleClearComplete = () => {
		debugLog(3, "[ScenarioConfigs][ACTION] handleClearComplete", {});
		props.onClearComplete();
	};

	// --- Logging: Render and State Snapshots ---
	// debugLog(2, "\n↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓\n");
	debugLog(9, "[ScenarioConfigs][FLOW] render", {
		renderCount: renderCount.current,
		currScenario: currScenario,
	});
	debugLog(9, "[ScenarioConfigs][FLOW] renderState", {
		props: props,
		pageMode: pageMode,
		sortedCfgTypes: sortedCfgTypes,
		allUsers: allUsers,
		allUserGroups: allUserGroups,
	});
	// debugLog(4, "\n↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑\n");

	return (
		<>
			<Box w="100%">
				<Stack gap={2}>
					{currScenario &&
						sortedCfgTypes.length !== 0 &&
						sortedCfgTypes.map((cfg, index) => {
							if (cfg.type_id === 1) {
								return <div key={cfg.id}></div>;
							} else if (cfg.type_id === 2) {
								return <div key={cfg.id}></div>;
							} else if (cfg.type_id === 3) {
								const allGroupItems = Array.isArray(allUserGroups) ? allUserGroups : [];
								// Keep PUBLIC as read-policy only; never expose it as an editable ACL target.
								const filteredGroupItems =
									cfg.code === "edit_access_group"
										? allGroupItems.filter(
												(group) =>
													Number(group?.id) !== -100 &&
													String(group?.group_code || "").toUpperCase() !== "PUBLIC",
											)
										: allGroupItems;
								const arrayItems = cfg.flag1 === 1 ? filteredGroupItems : allUsers;

								// Derive ids from the legacy access rows: [{ comp_value }]
								const selectedAccessIds =
									getValuesByPropertyPath(currScenario[`cfg_${cfg.code}`], "comp_value") ||
									[];
								return (
									<div key={cfg.id}>
										<FormMultiSelect
											mode={pageMode}
											type={cfg}
											items={arrayItems}
											selected={selectedAccessIds}
											onUpdate={updateAccessHandler}
										/>
									</div>
								);
							}
							return false;
						})}
				</Stack>
			</Box>
		</>
	);
};

export default ScenarioConfigs;
